"use client";

import React from "react";
import { ArrowRight, BookOpen, Clock, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import Reveal from "@/components/common/Reveal";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
const articlesCopy = {
  eyebrow: {
    en: "Engineering insights",
    zh: "工程技术洞察",
    es: "Conocimientos técnicos",
    de: "Technische Einblicke",
    fr: "Perspectives techniques",
    pt: "Perspectivas técnicas"
  },
  title: {
    en: "Related technical articles",
    zh: "相关技术文章",
    es: "Artículos técnicos relacionados",
    de: "Verwandte Fachartikel",
    fr: "Articles techniques associés",
    pt: "Artigos técnicos relacionados"
  },
  description: {
    en: "Explore calculations, material specifications, and operational guidance for industrial liquid and process filtration.",
    zh: "探索工业液体与工艺过滤的工程计算、材料规格和操作指南。",
    es: "Explore cálculos, especificaciones de materiales y guías operativas para la filtración de líquidos y procesos industriales.",
    de: "Erfahren Sie mehr über Berechnungen, Werkstoffspezifikationen und Praxisleitfäden für die industrielle Flüssigkeits- und Prozessfiltration.",
    fr: "Explorez les calculs d'ingénierie, les spécifications de matériaux et les guides pratiques pour la filtration des liquides et procédés.",
    pt: "Explore cálculos, especificações de materiais e orientações operacionais para filtração de líquidos e processos industriais."
  },
  viewAll: {
    en: "View all resources",
    zh: "查看所有资源",
    es: "Ver todos los recursos",
    de: "Alle Ressourcen ansehen",
    fr: "Voir toutes les ressources",
    pt: "Ver todos os recursos"
  },
  errorMsg: {
    en: "Unable to load technical articles.",
    zh: "无法加载技术文章。",
    es: "No se pudieron cargar los artículos técnicos.",
    de: "Fachartikel konnten nicht geladen werden.",
    fr: "Impossible de charger les articles techniques.",
    pt: "Não foi possível carregar os artigos técnicos."
  },
  tryAgain: {
    en: "Try again",
    zh: "重试",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  },
  noPostsTitle: {
    en: "No related articles found",
    zh: "未找到相关技术文章",
    es: "No se encontraron artículos relacionados",
    de: "Keine verwandten Fachartikel gefunden",
    fr: "Aucun article technique trouvé",
    pt: "Nenhum artigo técnico encontrado"
  },
  noPostsDesc: {
    en: "Check back shortly for updated filtration guides and engineering resources.",
    zh: "请稍后查看更新的工业过滤技术指南与工程资料。",
    es: "Vuelva a consultar pronto para ver guías de filtración y recursos actualizados.",
    de: "Besuchen Sie uns bald wieder für aktualisierte Filterleitfäden und technische Dokumente.",
    fr: "Revenez bientôt pour consulter nos guides de filtration et ressources techniques actualisés.",
    pt: "Volte em breve para conferir guias de filtração e recursos de engenharia atualizados."
  },
  defaultCategory: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  readPaper: {
    en: "Read technical paper",
    zh: "阅读技术论文",
    es: "Leer artículo técnico",
    de: "Fachbeitrag lesen",
    fr: "Lire l'article technique",
    pt: "Ler artigo técnico"
  },
  coverAlt: {
    en: "Filtration article cover",
    zh: "过滤技术文章封面",
    es: "Portada del artículo de filtración",
    de: "Titelbild des Filterartikels",
    fr: "Couverture de l'article de filtration",
    pt: "Capa do artigo de filtração"
  }
};
const dateLocales: Record<Lang, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  pt: "pt-BR"
};
interface ArticlesListProps {
  lang: Lang;
}
function ArticlesList({
  lang
}: ArticlesListProps) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-lg border border-border bg-card p-6">
            <Skeleton className="mb-4 h-48 w-full rounded-lg" />
            <div className="mb-3 flex items-center gap-2">
              <Skeleton className="h-5 w-20 rounded-md" />
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
            <Skeleton className="mb-2 h-6 w-5/6 rounded-md" />
            <Skeleton className="mb-4 h-16 w-full rounded-md" />
            <Skeleton className="mt-auto h-9 w-28 rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center">
        <p className="text-sm text-muted-foreground">
          {pick(lang, articlesCopy.errorMsg)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          {pick(lang, articlesCopy.tryAgain)}
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center">
        <BookOpen className="mb-3 size-10 text-muted-foreground stroke-[1.5]" />
        <p className="text-base font-medium text-foreground">
          {pick(lang, articlesCopy.noPostsTitle)}
        </p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {pick(lang, articlesCopy.noPostsDesc)}
        </p>
      </div>;
  }
  const locale = dateLocales[lang] || "en-US";
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : pick(lang, articlesCopy.defaultCategory);
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric"
      }) : null;
      return <Reveal key={post.id} delay={i * 60}>
            <Card data-index={i} className="group flex h-full flex-col justify-between overflow-hidden border border-border bg-card mf-hover-lift">
              <div>
                {post.featuredImage && <div className="mf-card-media relative mf-ratio-card w-full overflow-hidden">
                    <img src={post.featuredImage} alt={post.title || pick(lang, articlesCopy.coverAlt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </div>}

                <CardHeader className="gap-2.5 pt-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="font-normal mf-caption border border-border bg-muted">
                      <Tag className="mr-1 size-3.5" aria-hidden="true" />
                      {categoryName}
                    </Badge>
                    {formattedDate && <span className="flex items-center mf-caption text-muted-foreground">
                        <Clock className="mr-1 size-3.5" aria-hidden="true" />
                        {formattedDate}
                      </span>}
                  </div>
                  <CardTitle className="font-default text-lg font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary">
                    {post.title}
                  </CardTitle>
                </CardHeader>

                {post.excerpt && <CardContent>
                    <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt.replace(/<[^>]+>/g, "").trim()}
                    </p>
                  </CardContent>}
              </div>

              <CardFooter className="pt-2">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-primary hover:bg-transparent hover:text-primary/80">
                  <Link to={post.link || "#"}>
                    <span>{pick(lang, articlesCopy.readPaper)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </Reveal>;
    })}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  return <section id="relatedarticles" data-nav="light" className="bg-background mf-section border-t border-border">
      <div className="mf-container">
        <Reveal>
          <div className="mb-12 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <span className="mf-caption font-semibold uppercase tracking-wider text-primary">
                {pick(lang, articlesCopy.eyebrow)}
              </span>
              <h2 className="mt-2 font-default mf-h2 text-foreground">
                {pick(lang, articlesCopy.title)}
              </h2>
              <p className="mt-3 text-base text-muted-foreground max-w-[65ch]">
                {pick(lang, articlesCopy.description)}
              </p>
            </div>
            <div>
              <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
                <Link to="/TechnicalResourcesPage">
                  <span>{pick(lang, articlesCopy.viewAll)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesList lang={lang} />
        </PostsProvider>

        <div className="mt-10 flex justify-center sm:hidden">
          <Button asChild variant="outline" className="w-full">
            <Link to="/TechnicalResourcesPage">
              <span>{pick(lang, articlesCopy.viewAll)}</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}